import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * OnboardingChecklist — пошаговый чек-лист для директора при первом входе.
 * Шаги вычисляются автоматически от текущего состояния данных:
 *   1. Создана компания (есть company_id)
 *   2. Настроены статусы дней (>= 1 кастомный или хотя бы открыта вкладка)
 *   3. Добавлен хотя бы один сотрудник
 *   4. Налоги настроены (открыта вкладка хотя бы один раз)
 *
 * Если все шаги выполнены — чек-лист скрывается автоматически.
 * Пользователь может временно свернуть чек-лист — состояние хранится в localStorage.
 */
export default function OnboardingChecklist({ user, company, employeesCount }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const storageKey = `tabel.onboarding.dismissed.${user?.user_id || ""}.${company?.company_id || ""}`;
  const [dismissed, setDismissed] = useState(false);
  const [forceShow, setForceShow] = useState(false); // user explicitly clicked "Показать"

  useEffect(() => {
    setDismissed(localStorage.getItem(storageKey) === "1");
  }, [storageKey]);

  const isDirector = user?.role === "director";

  const steps = useMemo(() => {
    const hasCompany = !!company?.company_id;
    const hasStatuses = (company?.statuses || []).length >= 1;
    const hasEmployees = (employeesCount || 0) >= 2; // director himself counts as 1
    const hasTaxes = !!(company?.tax_config && (company.tax_config.ipn || company.tax_config.opv));
    return [
      {
        key: "company", title: t("onboarding.company_title"),
        description: t("onboarding.company_desc"),
        done: hasCompany,
        icon: "bi-building",
        action: () => navigate("/create-company"),
      },
      {
        key: "statuses", title: t("onboarding.statuses_title"),
        description: t("onboarding.statuses_desc"),
        done: hasStatuses,
        icon: "bi-tags",
        action: () => navigate("/settings"),
      },
      {
        key: "employees", title: t("onboarding.employees_title"),
        description: t("onboarding.employees_desc"),
        done: hasEmployees,
        icon: "bi-people",
        action: () => navigate("/employees"),
      },
      {
        key: "taxes", title: t("onboarding.taxes_title"),
        description: t("onboarding.taxes_desc"),
        done: hasTaxes,
        icon: "bi-cash-coin",
        action: () => navigate("/settings"),
      },
    ];
  }, [company, employeesCount, navigate, t]);

  if (!isDirector) return null;

  const doneCount = steps.filter(s => s.done).length;
  const allDone = doneCount === steps.length;
  const percent = Math.round((doneCount / steps.length) * 100);

  // Auto-collapse when all steps are done (but keep visible as small bar).
  // User can click "Показать" to force-expand even when all done.
  const shouldCollapse = (allDone || dismissed) && !forceShow;

  if (shouldCollapse) {
    return (
      <div className="alert alert-light d-flex align-items-center justify-content-between py-2 mb-3"
           data-testid="onboarding-collapsed">
        <div className="small">
          <i className="bi bi-list-check me-2"></i>
          <strong>{t("onboarding.setup")}:</strong>{" "}
          {allDone
            ? <span className="text-success"><i className="bi bi-check-circle-fill me-1"></i>{t("onboarding.all_done")}</span>
            : <>{t("onboarding.remaining_short", { done: doneCount, total: steps.length })}
                <span className="text-muted ms-2">· {t("onboarding.remaining_list", { items: steps.filter(s => !s.done).map(s => s.title.toLowerCase()).join(", ") })}</span></>}
        </div>
        <button className="btn btn-sm btn-link"
                onClick={() => { localStorage.removeItem(storageKey); setDismissed(false); setForceShow(true); }}
                data-testid="onboarding-expand">
          {t("onboarding.expand")}
        </button>
      </div>
    );
  }

  return (
    <div className="card card-soft mb-4 onboarding-card" data-testid="onboarding-checklist">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
          <div>
            <h5 className="fw-bold mb-1">
              <i className={`bi ${allDone ? "bi-patch-check-fill text-success" : "bi-stars text-warning"} me-2`}></i>
              {allDone ? t("onboarding.title_done") : t("onboarding.title_welcome")}
            </h5>
            <div className="text-muted small">
              {allDone ? t("onboarding.subtitle_done") : t("onboarding.subtitle_welcome")}
            </div>
          </div>
          <button className="btn btn-sm btn-link text-muted"
                  onClick={() => { localStorage.setItem(storageKey, "1"); setDismissed(true); setForceShow(false); }}
                  data-testid="onboarding-dismiss"
                  title={t("onboarding.collapse_title")}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className="mb-3">
          <div className="d-flex justify-content-between small text-muted mb-1">
            <span>{t("onboarding.progress")}</span>
            <span><strong>{doneCount}</strong> / {steps.length} ({percent}%)</span>
          </div>
          <div className="progress" style={{ height: 8 }}>
            <div className="progress-bar bg-success" role="progressbar"
                 style={{ width: `${percent}%` }} data-testid="onboarding-progress"></div>
          </div>
        </div>

        <div className="row g-2">
          {steps.map((s, i) => (
            <div className="col-md-6" key={s.key}>
              <button
                className={`onboarding-step btn w-100 text-start d-flex align-items-center gap-3 p-3 ${s.done ? "onboarding-step-done" : "onboarding-step-todo"}`}
                onClick={s.done ? undefined : s.action}
                disabled={s.done}
                tabIndex={s.done ? -1 : 0}
                title={s.done ? t("onboarding.step_done_title") : ""}
                data-testid={`onboarding-step-${s.key}`}
              >
                <div className={`onboarding-step-icon ${s.done ? "bg-success" : "bg-primary"}`}>
                  {s.done
                    ? <i className="bi bi-check-lg text-white"></i>
                    : <span className="text-white fw-bold">{i + 1}</span>}
                </div>
                <div className="flex-grow-1 min-w-0">
                  <div className={`fw-semibold ${s.done ? "text-muted" : ""}`}>
                    <i className={`bi ${s.icon} me-1 small`}></i>
                    {s.title}
                  </div>
                  <div className="small text-muted text-truncate">{s.description}</div>
                </div>
                {!s.done && <i className="bi bi-arrow-right text-primary"></i>}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
